<script lang="ts" setup>
import { composeSharedSpaceEmailBody } from "@/util/sharedSpaceEmail";
import {
  ref,
  computed,
  nextTick,
  onBeforeUnmount,
  PropType,
  watch,
  toRef,
} from "vue";
import { useStore } from "vuex";
import { useI18n } from "vue-i18n";
import { DocumentSpace } from "@/graphql/operations";
import { MenuOverlay, Tooltip, CRMContactSelectionModal } from "@/components";
import {
  buildClipboardDocumentTitle,
  buildDocumentLink,
  buildSharedSpaceClipboardPayload,
  buildSpaceLink,
  copyClipboardPayload,
  fetchCRMConsentStatus,
  hasCommunicationOptIn,
  isSharedSpaceConsentGateError,
  resolveDirectPreviewUrlPrefix,
  shouldUseAppHostForDocumentLinks,
  type SharedSpaceConsentFailureReason,
  type SharedSpaceSectionEntry,
} from "@/util";
import graphqlClient from "@/graphql/client";
import MissingConsentModal from "../MissingConsentModal.vue";
import MailModal from "../MailModal.vue";
import ManualSharingConsentModal from "../ManualSharingConsentModal.vue";
import { useManualSharingConsent } from "@/composables/useManualSharingConsent";
import { useManualSharingConfirmation } from "@/composables/useManualSharingConfirmation";
import ConsentCheckModal from "../ConsentCheckModal.vue";
import EmailVerificationConfig from "./EmailVerificationConfig.vue";
import { useDocumentSpaceSharing } from "@/composables";

type DocumentSpaceCodeQueryResult = {
  documentSpace?: {
    id?: string | null;
    codeOnAccessCard?: string | null;
    tenant?: {
      spaceDomain?: string | null;
    } | null;
  } | null;
};

type GenerateCodeForAccessCardMutationResult = {
  generateCodeForAccessCard?: {
    documentSpace?: {
      id?: string | null;
      codeOnAccessCard?: string | null;
    } | null;
  } | null;
};

type UpdateAccessCardCodeMutationResult = {
  updateDocumentSpace?: {
    documentSpace?: {
      id?: string | null;
      codeOnAccessCard?: string | null;
    } | null;
  } | null;
};

const DOCUMENT_SPACE_CODE_QUERY = `
  query DocumentSpaceCode($id: BigInt!) {
    documentSpace(id: $id) {
      id
      codeOnAccessCard
      tenant {
        spaceDomain
      }
    }
  }
`;

const GENERATE_CODE_FOR_ACCESS_CARD_MUTATION = `
  mutation GenerateCodeForAccessCard($spaceId: BigInt!, $failIfBlank: Boolean) {
    generateCodeForAccessCard(input: { spaceId: $spaceId, failIfBlank: $failIfBlank }) {
      documentSpace {
        id
        codeOnAccessCard
      }
    }
  }
`;

const UPDATE_ACCESS_CARD_CODE_MUTATION = `
  mutation UpdateAccessCardCode($id: BigInt!, $codeOnAccessCard: String) {
    updateDocumentSpace(
      input: { id: $id, patch: { codeOnAccessCard: $codeOnAccessCard } }
    ) {
      documentSpace {
        id
        codeOnAccessCard
      }
    }
  }
`;

const props = defineProps({
  documentSpace: {
    type: Object as PropType<DocumentSpace>,
    required: true,
  },
  emailVerificationValid: {
    type: Boolean,
    required: true,
  },
});

const emit = defineEmits(["refreshData", "update:emailVerificationValid"]);

const store = useStore();
const user = store.getters.user;
const { t } = useI18n();

const documentSpaceRef = toRef(props, "documentSpace");
const emailVerificationValid = computed(() => props.emailVerificationValid);
const {
  crmContact,
  needsEmail,
  displayEmailVerification,
  displayShareOptions,
  checkingConsent,
  sharedSpaceUrlPrefix,
  defaultSpacePrefix,
  hasBusinessCardVariants,
  generateLink,
  generateVoucherSegment,
} = useDocumentSpaceSharing({
  documentSpace: documentSpaceRef,
  emailVerificationValid,
});

const confirmation = useManualSharingConfirmation();
const {
  active: manualConsentActive,
  phase: manualConsentPhase,
  retry: retryCopy,
  request: requestCopy,
  confirm: confirmCopy,
  cancel: cancelCopy,
} = useManualSharingConsent({
  required: () => user.hasRequiredManualConsentForSharing(),
  eligible: () => displayShareOptions.value,
  context: () =>
    JSON.stringify([
      props.documentSpace.id,
      props.documentSpace.allowedEmailAddresses,
      props.documentSpace.config?.requiresEmailAuthentication,
      crmContact.value,
      props.documentSpace.documentSpaceSections,
      sharedSpaceUrlPrefix.value,
      user.mergedConfig,
      user.hasAdvancedClipboard(),
      confirmation.locale.value,
    ]),
});

const hasCRMMapping = computed(
  () => crmContact.value !== null && crmContact.value !== undefined,
);

const hasCodeCard = computed(() => user.hasCodeCard());
const advancedClipboardEnabled = computed(() => user.hasAdvancedClipboard());
const canSendSpaceMail = computed(() => store.getters.user.canSendSpaceMail());
const accessCardCode = ref<string | null>(null);
const loadingAccessCardCode = ref<boolean>(false);
const updatingAccessCardCode = ref<boolean>(false);
// The tenant's visitor-facing code-card domain (settings.tenants_code_card.domain),
// exposed via the Tenant.spaceDomain computed column. Codes only resolve on that host;
// nginx redirects the clean root URL to the code-entry page.
const codeCardSpaceDomain = ref<string | null>(null);
const formattedAccessCardCode = computed(() =>
  accessCardCode.value ? accessCardCode.value.toUpperCase() : null,
);
const codeCardEntryUrl = computed(() =>
  codeCardSpaceDomain.value ? `https://${codeCardSpaceDomain.value}/` : null,
);

const campaignLinks = computed(
  () => crmContact.value?.crmContactPhaseMappingsByContactId?.nodes,
);
const DEFAULT_SPACE_PREFIX = `${window.location.origin}/2/`;

const onEmailValidityChange = (valid: boolean) => {
  emit("update:emailVerificationValid", valid);
};

const documentSpaceNameForClipboard = (): string => {
  const spaceName = props.documentSpace.name?.trim();
  if (spaceName && spaceName.length > 0) {
    return spaceName;
  }

  return t("documentSpace.copyLink.unnamedSpace");
};

const shareButton = ref<HTMLElement | null>(null);
const previewButton = ref<HTMLElement | null>(null);
const previewGenerating = ref(false);
const directPreviewError = ref<string | null>(null);
let previewRequest = 0;
let componentUnmounted = false;
const directPreviewUrlPrefix = computed(() =>
  resolveDirectPreviewUrlPrefix(
    defaultSpacePrefix.value,
    sharedSpaceUrlPrefix.value,
  ),
);
const copyCodeButton = ref<HTMLElement | null>(null);
const shareMenuActive = ref<boolean>(false);
const showTooltip = (element: HTMLElement | null, message: string) => {
  element?.setAttribute("data-tooltip", message);
  window.setTimeout(() => {
    element?.removeAttribute("data-tooltip");
  }, 1000);
};
const showShareButtonTooltip = (message: string) => {
  showTooltip(shareButton.value, message);
};
const showPreviewButtonTooltip = (message: string) => {
  showTooltip(previewButton.value, message);
};
const showCopyCodeTooltip = (message: string) => {
  showTooltip(copyCodeButton.value, message);
};
const loadAccessCardCode = async () => {
  if (!hasCodeCard.value) {
    return;
  }

  loadingAccessCardCode.value = true;
  try {
    const result = await graphqlClient
      .query(
        DOCUMENT_SPACE_CODE_QUERY,
        { id: props.documentSpace.id },
        { requestPolicy: "network-only" },
      )
      .toPromise();

    if (result.error) {
      showShareButtonTooltip(t("documentSpace.codeCard.loadError"));
      return;
    }

    const documentSpace = (
      result.data as DocumentSpaceCodeQueryResult | undefined
    )?.documentSpace;
    accessCardCode.value = documentSpace?.codeOnAccessCard ?? null;
    codeCardSpaceDomain.value = documentSpace?.tenant?.spaceDomain ?? null;
  } finally {
    loadingAccessCardCode.value = false;
  }
};
const toggleShareMenu = () => {
  shareMenuActive.value = !shareMenuActive.value;

  if (shareMenuActive.value) {
    loadAccessCardCode();
  }
};

const closeShareMenuAndFocusTrigger = async () => {
  shareMenuActive.value = false;
  await nextTick();
  shareButton.value?.focus();
};

const mailModalActive = ref<boolean>(false);
const displayMailModal = async () => {
  await closeShareMenuAndFocusTrigger();
  mailModalActive.value = true;
};

const openPreview = async (urlPrefix?: string, closeMenu = true) => {
  if (previewGenerating.value) {
    return;
  }
  const requestedSpaceId = props.documentSpace.id;
  const request = ++previewRequest;
  if (!closeMenu) {
    directPreviewError.value = null;
  }
  previewGenerating.value = true;
  try {
    const url = await generateLink(urlPrefix, true);
    if (
      componentUnmounted ||
      request !== previewRequest ||
      props.documentSpace.id !== requestedSpaceId
    )
      return;
    window.open(url, "_blank")?.focus();
    if (closeMenu) {
      toggleShareMenu();
    }
  } catch (err) {
    if (
      componentUnmounted ||
      request !== previewRequest ||
      props.documentSpace.id !== requestedSpaceId
    )
      return;
    const message = t("documentSpace.copyLink.tooltipErrorGenerate");
    if (closeMenu) {
      showShareButtonTooltip(message);
    } else {
      directPreviewError.value = message;
      showPreviewButtonTooltip(message);
    }
  } finally {
    if (request === previewRequest) {
      previewGenerating.value = false;
    }
  }
};

const openDirectPreview = () =>
  openPreview(directPreviewUrlPrefix.value, false);

const clipboardTypeLabels = computed(() => ({
  video: t("documentSpace.copyLink.typeLabels.video"),
  image: t("documentSpace.copyLink.typeLabels.image"),
}));

const sectionsForClipboard = (
  urlPrefix: string | undefined,
  urlSegment: string,
  space: DocumentSpace,
): Array<SharedSpaceSectionEntry> => {
  const useAppHostForDocuments = shouldUseAppHostForDocumentLinks({
    urlPrefix,
    forcePitchviewPrefix: user.forcesPitchviewPrefixForDocLinks(),
  });
  const sections = space.documentSpaceSections?.nodes || [];

  return sections
    .map((section) => {
      const sectionTitle = section?.title || t("documentSpace.documents.title");
      const documents = (section?.documentSpaceItemsBySectionId?.nodes || [])
        .map((documentItem) => {
          const spaceItemId = documentItem?.id;
          const document = documentItem?.document;
          const flow = documentItem?.flow;
          const documentTitle = document?.displayTitle;

          if (!spaceItemId || (!documentTitle && !flow?.title)) {
            return null;
          }

          const url = buildDocumentLink({
            urlPrefix,
            urlSegment,
            spaceItemId,
            fallbackPrefix: DEFAULT_SPACE_PREFIX,
            useAppHostForDocuments,
          });

          if (flow?.title) {
            return {
              title: `${flow.title} (${t("customFlows.itemType")})`,
              url,
            };
          }

          return {
            title: buildClipboardDocumentTitle({
              displayTitle: documentTitle!,
              mimeType: document!.mimeType,
              originalMimeType: document!.originalMimeType,
              typeLabels: clipboardTypeLabels.value,
            }),
            url,
          };
        })
        .filter(
          (
            entry,
          ): entry is {
            title: string;
            url: string;
          } => entry !== null,
        );

      return {
        sectionTitle,
        documents,
      };
    })
    .filter((section) => section.documents.length > 0);
};

const copyLink = async (urlPrefix?: string, linkId?: string) => {
  const space = props.documentSpace;
  const contactId = crmContact.value?.id;
  const advanced = advancedClipboardEnabled.value && !linkId;
  const templateText = user.mergedConfig?.default_email_body;
  const sourceLineText = t("documentSpace.copyLink.insertedFromLine", {
    spaceName: documentSpaceNameForClipboard(),
  });
  await closeShareMenuAndFocusTrigger();
  // A route change during the menu's nextTick must not queue the new space.
  if (
    componentUnmounted ||
    props.documentSpace.id !== space.id ||
    crmContact.value?.id !== contactId
  )
    return;
  return requestCopy({
    persist: confirmation.forTarget({
      documentSpaceId: space.id,
      expectedCrmContactId: contactId ?? null,
      campaignPhaseLinkId: linkId ?? null,
    }),
    prepare: async (signal) => {
      if (advanced) {
        const urlSegment = await generateVoucherSegment(
          false,
          undefined,
          signal,
        );
        signal.throwIfAborted();
        const mainLink = buildSpaceLink({
          urlPrefix,
          urlSegment,
          fallbackPrefix: DEFAULT_SPACE_PREFIX,
        });
        const payload = buildSharedSpaceClipboardPayload({
          mainLink,
          mainLinkText: t("documentSpace.copyLink.richMainLinkText"),
          sections: sectionsForClipboard(urlPrefix, urlSegment, space),
          templateText,
          sourceLineText,
        });
        return () => copyClipboardPayload(payload, signal);
      }
      const link = await generateLink(urlPrefix, false, linkId, signal);
      return () => navigator.clipboard.writeText(link);
    },
    onSuccess: () =>
      showShareButtonTooltip(t("documentSpace.copyLink.tooltip")),
    onError: (err, phase) => {
      if (isSharedSpaceConsentGateError(err)) {
        showMissingConsentModal(err.reason);
        return true;
      }
      if (!user.hasRequiredManualConsentForSharing())
        showShareButtonTooltip(
          phase === "copying"
            ? t("documentSpace.copyLink.tooltipErrorCopy")
            : t("documentSpace.copyLink.tooltipErrorGenerate"),
        );
      return false;
    },
  });
};

const copyAccessCardCode = async () => {
  if (!accessCardCode.value) {
    return;
  }

  try {
    await navigator.clipboard.writeText(accessCardCode.value);
    showCopyCodeTooltip(t("documentSpace.codeCard.copySuccess"));
  } catch (err) {
    showCopyCodeTooltip(t("documentSpace.codeCard.copyError"));
  }
};

const generateAccessCardCode = async () => {
  if (updatingAccessCardCode.value) {
    return;
  }

  if (
    accessCardCode.value &&
    !window.confirm(t("documentSpace.codeCard.confirmRegenerate"))
  ) {
    return;
  }

  updatingAccessCardCode.value = true;
  try {
    const result = await graphqlClient
      .mutation(GENERATE_CODE_FOR_ACCESS_CARD_MUTATION, {
        spaceId: props.documentSpace.id,
        failIfBlank: false,
      })
      .toPromise();

    if (result.error) {
      showShareButtonTooltip(t("documentSpace.codeCard.generateError"));
      return;
    }

    accessCardCode.value =
      (result.data as GenerateCodeForAccessCardMutationResult | undefined)
        ?.generateCodeForAccessCard?.documentSpace?.codeOnAccessCard ?? null;
    showShareButtonTooltip(t("documentSpace.codeCard.generateSuccess"));
  } finally {
    updatingAccessCardCode.value = false;
  }
};

const clearAccessCardCode = async () => {
  if (!accessCardCode.value || updatingAccessCardCode.value) {
    return;
  }

  if (!window.confirm(t("documentSpace.codeCard.confirmDelete"))) {
    return;
  }

  updatingAccessCardCode.value = true;
  try {
    const result = await graphqlClient
      .mutation(UPDATE_ACCESS_CARD_CODE_MUTATION, {
        id: props.documentSpace.id,
        codeOnAccessCard: null,
      })
      .toPromise();

    if (result.error) {
      showShareButtonTooltip(t("documentSpace.codeCard.deleteError"));
      return;
    }

    accessCardCode.value =
      (result.data as UpdateAccessCardCodeMutationResult | undefined)
        ?.updateDocumentSpace?.documentSpace?.codeOnAccessCard ?? null;
    showShareButtonTooltip(t("documentSpace.codeCard.deleteSuccess"));
  } finally {
    updatingAccessCardCode.value = false;
  }
};

const openMailtoLink = async (urlPrefix?: string) => {
  await closeShareMenuAndFocusTrigger();

  let spaceLink: string;
  try {
    spaceLink = await generateLink(urlPrefix, false);
  } catch (err) {
    if (isSharedSpaceConsentGateError(err)) {
      showMissingConsentModal(err.reason);
      return;
    }
    showShareButtonTooltip(t("documentSpace.copyLink.tooltipErrorGenerate"));
    return;
  }

  // Get default text from user config or use translated defaults
  const subject =
    user.mergedConfig?.default_email_subject ??
    t("documentSpace.mail.subject.default");

  const bodyText =
    user.mergedConfig?.default_email_body ??
    t("documentSpace.mail.body.default");

  const fullBody = composeSharedSpaceEmailBody(bodyText, spaceLink);

  // Use CRM contact email as recipient if available
  const recipient = crmContact.value?.email || "";

  // Create mailto link with recipient if available
  const mailtoLink = `mailto:${recipient}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(fullBody)}`;

  // Open in default mail client
  window.location.href = mailtoLink;
};

const CRMContactSelectionModalActive = ref<boolean>(false);
const missingConsentModalActive = ref<boolean>(false);
const consentFailureReason = ref<SharedSpaceConsentFailureReason>("missing");

const showMissingConsentModal = (reason: SharedSpaceConsentFailureReason) => {
  consentFailureReason.value = reason;
  missingConsentModalActive.value = true;
};

const closeMissingConsentModal = async () => {
  missingConsentModalActive.value = false;
  await nextTick();
  shareButton.value?.focus();
};

const consentIsMissing = ref<boolean | null>(null);
const checkConsent = async () => {
  if (!store.getters.user.hasSignConsent() || !crmContact.value) {
    consentIsMissing.value = false;
    return;
  }

  try {
    const consentStatus = await fetchCRMConsentStatus(crmContact.value.id);
    consentIsMissing.value = !hasCommunicationOptIn(consentStatus);
  } catch (e) {
    //TODO handle
  }
};

checkConsent();

watch(
  () => props.documentSpace,
  () => {
    checkConsent();
  },
);

watch(
  () => props.documentSpace.id,
  (spaceId, previousSpaceId) => {
    if (spaceId === previousSpaceId) {
      return;
    }

    previewRequest += 1;
    previewGenerating.value = false;
    accessCardCode.value = null;
    directPreviewError.value = null;
    if (shareMenuActive.value) {
      loadAccessCardCode();
    }
  },
);

onBeforeUnmount(() => {
  componentUnmounted = true;
  previewRequest += 1;
});
</script>

<template>
  <div class="menu">
    <button
      type="button"
      class="button action_button preview_action"
      data-test="direct-preview-trigger"
      ref="previewButton"
      :disabled="previewGenerating"
      :aria-busy="previewGenerating"
      :aria-invalid="directPreviewError ? 'true' : undefined"
      :aria-describedby="
        directPreviewError ? 'classic-preview-error' : undefined
      "
      @click="openDirectPreview"
    >
      <span
        class="material-symbols-outlined"
        :class="{ preview_spinner: previewGenerating }"
      >
        {{ previewGenerating ? "progress_activity" : "open_in_new" }}
      </span>
      {{ $t("documentSpace.preview.actionText") }}
    </button>
    <span
      v-if="directPreviewError"
      id="classic-preview-error"
      data-test="classic-preview-error"
      class="visually_hidden"
      role="alert"
    >
      {{ directPreviewError }}
    </span>
    <button
      type="button"
      class="button action_button share_action"
      data-test="share-menu-trigger"
      @click="toggleShareMenu"
      ref="shareButton"
    >
      <span class="material-symbols-outlined"> ios_share </span>
      {{ $t("documentSpace.share.buttonText") }}
    </button>
  </div>

  <menu-overlay
    :active="shareMenuActive"
    :refElem="shareButton"
    @close="shareMenuActive = false"
  >
    <template #header>{{ $t("documentSpace.share.label") }}</template>

    <template #body>
      <div class="menu_section border">
        <div class="menu_title">
          {{ $t("documentSpace.preview.title") }}
        </div>

        <template v-if="hasBusinessCardVariants">
          <div class="button_container">
            <button
              class="button share_button"
              :disabled="previewGenerating"
              @click="openPreview(defaultSpacePrefix ?? undefined)"
            >
              <span class="material-symbols-outlined">open_in_new</span>
              {{ $t("documentSpace.preview.buttonText") }}
            </button>
          </div>
          <div class="button_container">
            <button
              class="button share_button"
              :disabled="previewGenerating"
              @click="openPreview(sharedSpaceUrlPrefix)"
            >
              <span class="material-symbols-outlined">open_in_new</span>
              {{ $t("documentSpace.preview.buttonTextBusinessCard") }}
            </button>
          </div>
        </template>

        <template v-else>
          <button
            class="button share_button"
            :disabled="previewGenerating"
            @click="openPreview(sharedSpaceUrlPrefix)"
          >
            <span class="material-symbols-outlined">open_in_new</span>
            {{ $t("documentSpace.preview.buttonText") }}
          </button>
          <p class="info">{{ $t("documentSpace.preview.info") }}</p>
        </template>
      </div>

      <div v-if="hasCodeCard" class="menu_section border">
        <div class="menu_title">
          {{ $t("documentSpace.codeCard.title") }}
        </div>

        <div class="code_card_value_label">
          {{ $t("documentSpace.codeCard.codeLabel") }}
        </div>
        <div v-if="formattedAccessCardCode" class="code_card_value">
          {{ formattedAccessCardCode }}
        </div>
        <p v-else-if="!loadingAccessCardCode" class="info">
          {{ $t("documentSpace.codeCard.missingCode") }}
        </p>
        <p v-if="loadingAccessCardCode" class="info">
          {{ $t("documentSpace.codeCard.loading") }}
        </p>

        <div
          v-if="formattedAccessCardCode"
          class="code_card_actions"
          data-test="code-card-actions"
        >
          <Tooltip>
            <template v-slot>
              <button
                ref="copyCodeButton"
                class="button code_card_icon_button"
                data-test="code-card-copy-button"
                :disabled="loadingAccessCardCode || updatingAccessCardCode"
                :aria-label="$t('documentSpace.codeCard.copyCode')"
                @click="copyAccessCardCode"
              >
                <span class="material-symbols-outlined">content_copy</span>
              </button>
            </template>
            <template #tooltip>
              {{ $t("documentSpace.codeCard.copyCode") }}
            </template>
          </Tooltip>

          <Tooltip>
            <template v-slot>
              <button
                class="button code_card_icon_button"
                data-test="code-card-regenerate-button"
                :disabled="loadingAccessCardCode || updatingAccessCardCode"
                :aria-label="$t('documentSpace.codeCard.regenerateCode')"
                @click="generateAccessCardCode"
              >
                <span class="material-symbols-outlined">autorenew</span>
              </button>
            </template>
            <template #tooltip>
              {{ $t("documentSpace.codeCard.regenerateCode") }}
            </template>
          </Tooltip>

          <Tooltip>
            <template v-slot>
              <button
                class="button code_card_icon_button"
                data-test="code-card-delete-button"
                :disabled="loadingAccessCardCode || updatingAccessCardCode"
                :aria-label="$t('documentSpace.codeCard.deleteCode')"
                @click="clearAccessCardCode"
              >
                <span class="material-symbols-outlined">delete</span>
              </button>
            </template>
            <template #tooltip>
              {{ $t("documentSpace.codeCard.deleteCode") }}
            </template>
          </Tooltip>
        </div>

        <div v-else-if="!loadingAccessCardCode" class="button_container">
          <button
            class="button share_button"
            :disabled="updatingAccessCardCode"
            @click="generateAccessCardCode"
          >
            <span class="material-symbols-outlined">autorenew</span>
            {{ $t("documentSpace.codeCard.generateCode") }}
          </button>
        </div>

        <p v-if="codeCardEntryUrl" class="info">
          {{ $t("documentSpace.codeCard.infoPrefix") }}
          <a :href="codeCardEntryUrl" target="_blank" rel="noopener">
            {{ $t("documentSpace.codeCard.entryPageLink") }}
          </a>
          {{ $t("documentSpace.codeCard.infoSuffix") }}
        </p>
        <p v-else class="info">
          {{ $t("documentSpace.codeCard.entryPageUnavailable") }}
        </p>
      </div>

      <!-- share buttons for users with crm integration -->
      <!-- campaigns and mail only work with crm integration -->
      <template v-if="user.hasCRMIntegration()">
        <!-- display crm contact information -->
        <template v-if="crmContact">
          <div class="menu_section">
            <div class="menu_title">
              {{ $t("documentSpace.crmMapping.title") }}
            </div>
            <button class="button share_button not_clickable">
              <span class="material-symbols-outlined"> account_circle </span>
              {{ $t("documentSpace.crmMapping.tooltipLabel.name", crmContact) }}
            </button>

            <Tooltip class="right" v-if="consentIsMissing">
              <template v-slot>
                <p class="info">
                  <span class="material-symbols-outlined"> warning</span>
                  {{ $t("documentSpace.missingConsent.missing.title") }}
                </p>
              </template>
              <template #tooltip>
                {{ $t("documentSpace.missingConsent.missing.message") }}
              </template>
            </Tooltip>
          </div>
        </template>

        <!-- no crm contact selected -->
        <template v-else>
          <div class="menu_section">
            <div class="menu_title">
              {{ $t("documentSpace.crmMapping.title") }}
            </div>
            <Tooltip class="right">
              <template v-slot>
                <button
                  class="button share_button"
                  @click="CRMContactSelectionModalActive = true"
                >
                  <span class="material-symbols-outlined">
                    account_circle
                  </span>
                  {{ $t("documentSpace.crmMapping.button.text") }}
                </button>
              </template>
              <template #tooltip>
                {{ $t("documentSpace.crmMapping.button.info") }}
              </template>
            </Tooltip>
          </div>
        </template>

        <EmailVerificationConfig
          v-if="
            displayEmailVerification &&
            (!user.hasRequiredCRMContactForSharing() || crmContact)
          "
          :documentSpaceId="documentSpace.id"
          :allowedEmailAddresses="
            (documentSpace.allowedEmailAddresses || []).filter(
              (e): e is string => !!e,
            )
          "
          :required="needsEmail"
          :crmContactEmail="crmContact?.email ?? undefined"
          @update:valid="onEmailValidityChange"
        />
      </template>

      <!-- crm contact is already assigned or not required for sharing -->
      <template v-if="displayShareOptions">
        <div class="menu_section">
          <div class="menu_title">
            {{ $t("documentSpace.share.title") }}
          </div>

          <!-- share shortcuts -->
          <template v-if="hasBusinessCardVariants">
            <div class="button_container">
              <button
                class="button share_button"
                data-test="copy-space-link-plain-variant"
                @click="copyLink(defaultSpacePrefix ?? undefined)"
              >
                <span class="material-symbols-outlined">content_copy</span>
                {{ $t("documentSpace.copyLink.buttonText") }}
              </button>
            </div>
            <div class="button_container">
              <button
                class="button share_button"
                data-test="copy-space-link-business-variant"
                @click="copyLink(sharedSpaceUrlPrefix)"
              >
                <span class="material-symbols-outlined">content_copy</span>
                {{ $t("documentSpace.copyLink.buttonTextBusinessCard") }}
              </button>
            </div>
            <div class="button_container">
              <button
                class="button share_button"
                @click="openMailtoLink(sharedSpaceUrlPrefix)"
              >
                <span class="material-symbols-outlined">email</span>
                {{ $t("documentSpace.emailLink.buttonText") }}
              </button>
            </div>
          </template>
          <template v-else>
            <button
              class="button share_button"
              data-test="copy-space-link"
              @click="copyLink(sharedSpaceUrlPrefix)"
            >
              <span class="material-symbols-outlined">content_copy</span>
              {{ $t("documentSpace.copyLink.buttonText") }}
            </button>

            <button
              class="button share_button"
              data-test="share-email-link"
              @click="openMailtoLink(sharedSpaceUrlPrefix)"
            >
              <span class="material-symbols-outlined">email</span>
              {{ $t("documentSpace.emailLink.buttonText") }}
            </button>
          </template>

          <template v-if="user.hasCRMIntegration()">
            <template v-for="phase in campaignLinks">
              <div
                class="button_container"
                v-for="link in phase?.phase?.campaignPhaseLinksByPhaseId?.nodes"
                :key="link!.id"
              >
                <button
                  class="button share_button"
                  @click="copyLink(sharedSpaceUrlPrefix, link!.id)"
                >
                  <span class="material-symbols-outlined"> share </span>
                  {{ link!.name }}
                </button>
              </div>
            </template>

            <div class="button_container" v-if="canSendSpaceMail">
              <button
                class="button share_button"
                data-test="send-space-mail"
                @click="displayMailModal"
              >
                <span class="material-symbols-outlined">mail</span>
                {{ $t("documentSpace.sendMail.buttonText") }}
              </button>
            </div>
          </template>
        </div>
      </template>
    </template>
  </menu-overlay>

  <ManualSharingConsentModal
    v-if="manualConsentActive"
    :phase="manualConsentPhase"
    @retry="retryCopy"
    @confirm="confirmCopy"
    @close="cancelCopy"
  />

  <ConsentCheckModal v-if="checkingConsent" />

  <missing-consent-modal
    v-if="missingConsentModalActive"
    :reason="consentFailureReason"
    @close="closeMissingConsentModal"
  />

  <mail-modal
    v-if="mailModalActive"
    :documentSpace="documentSpace"
    :emailVerificationValid="emailVerificationValid"
    :crmContact="crmContact!"
    @close="mailModalActive = false"
  />

  <CRMContactSelectionModal
    v-if="CRMContactSelectionModalActive"
    :documentSpaceId="documentSpace.id"
    :documentSpaceName="documentSpace.name as string"
    :inherit-classic-theme="true"
    @success="emit('refreshData')"
    @close="CRMContactSelectionModalActive = false"
  />
</template>

<style scoped lang="scss">
.menu {
  margin-left: 1em;
  display: flex;
  align-items: center;
  gap: 0.5em;
}

.action_button {
  font-weight: 600;
}

.preview_action {
  background-color: var(--classic-surface, #fff);
  border-color: var(--classic-border-strong, var(--classic-border, #dbdbdb));
  color: var(--classic-text-secondary, var(--classic-text, #363636));
}

.preview_action:hover:not(:disabled) {
  background-color: var(--classic-surface-hover, #f7f5f2);
  border-color: var(--classic-border-strong, var(--classic-border, #b5b5b5));
  color: var(--classic-text-strong, var(--classic-text, #202020));
}

.share_action {
  background-color: var(--classic-brand-primary, #e30074);
  border-color: var(--classic-brand-primary, #e30074);
  color: var(--classic-on-accent, #fff);
}

.share_action:hover:not(:disabled) {
  background-color: color-mix(
    in srgb,
    var(--classic-brand-primary, #e30074) 85%,
    var(--classic-text-strong, #202020)
  );
  border-color: color-mix(
    in srgb,
    var(--classic-brand-primary, #e30074) 85%,
    var(--classic-text-strong, #202020)
  );
}

.action_button:focus-visible {
  outline: 2px solid var(--classic-focus, var(--classic-brand-primary, #e30074));
  outline-offset: 2px;
}

.preview_spinner {
  animation: preview-spin 0.8s linear infinite;
}

@keyframes preview-spin {
  to {
    transform: rotate(360deg);
  }
}

.menu_overlay {
  min-width: 350px;
}

.button_container {
  margin-top: 1em;
}

div .button_container:first-child {
  margin-top: 0;
}

.share_button {
  border: 1px solid var(--classic-border, #00000024);
  padding: 0.9em;
  border-radius: 0;
  width: 100%;
  justify-content: flex-start;
  font-weight: 600;
  min-width: 180px;
}

.share_button:hover {
  background-color: var(--classic-surface-hover, #f7f5f2);
}

.share_button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.share_button.not_clickable,
.share_button.not_clickable:hover {
  background-color: var(--classic-surface, white);
  pointer-events: none;
}

button[data-tooltip]::before {
  background: var(--classic-tooltip-surface, rgba(74, 74, 74, 0.9));
  border-radius: 2px;
  content: attr(data-tooltip);
  padding: 0.5rem 1rem;
  text-overflow: ellipsis;
  white-space: pre;
  left: 50%;
  top: 100%;
  margin-top: 7px;
  transform: translateX(-50%);
}

button[data-tooltip]::before,
button[data-tooltip]::after {
  box-sizing: border-box;
  color: #fff;
  display: inline-block;
  font-size: 0.75rem;
  overflow: hidden;
  pointer-events: none;
  position: absolute;
  z-index: 1020;
}

button[data-tooltip]::after {
  content: "";
  border-style: solid;
  border-width: 0 6px 6px 6px;
  border-color: transparent transparent
    var(--classic-tooltip-surface, rgba(74, 74, 74, 0.9)) transparent;
  left: 50%;
  top: calc(100% + 6px);
  transform: translateX(-50%);
}

button[data-tooltip] {
  overflow: visible;
  position: relative;
}

.visually_hidden {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

.menu_section {
  padding-top: 0.8em;
  padding-bottom: 0.8em;
}

.menu_section.border {
  border-bottom: 1px solid var(--classic-border, #e0e0e0);
  padding-bottom: 1em;
  margin-bottom: 0.2em;
}

.menu_title {
  color: var(--classic-text-muted, #9b9b9b);
  font-weight: 500;
  text-transform: uppercase;
  margin-bottom: 0.2em;
  font-size: 0.9em;
}

.code_card_value_label {
  color: var(--classic-text-secondary, #7a7a7a);
  font-size: 0.85em;
  margin-top: 0.35em;
}

.code_card_value {
  margin-top: 0.35em;
  border: 1px dashed var(--classic-border-strong, #cfd6de);
  background: var(--classic-surface, #ffffff);
  color: var(--classic-text-strong, #2b2b2b);
  font-size: 1.4em;
  font-weight: 700;
  letter-spacing: 0.18em;
  padding: 0.35em 0.5em;
}

.code_card_actions {
  display: flex;
  gap: 0.5em;
  margin-top: 0.75em;
}

.code_card_icon_button {
  border: 1px solid var(--classic-border, #00000024);
  border-radius: 0;
  width: 2.4rem;
  min-width: 2.4rem;
  height: 2.4rem;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--classic-surface, #ffffff);
}

.code_card_icon_button .material-symbols-outlined {
  margin-right: 0;
  line-height: 1;
}

.code_card_icon_button:hover {
  background-color: var(--classic-surface-hover, #f7f5f2);
}

.info {
  font-size: 0.9em;
  margin: 0.5em 0 0 0;
}

.share_button + .share_button {
  margin-top: 0.5em;
}
</style>
